import axios, { AxiosRequestConfig } from 'axios';

// Ducks
import { updateSessionErrorCount } from '@/ducks/user';

// Types
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'HEAD';

// Axios Instance
const axiosInstance = axios.create( {
    withCredentials: true
    , headers: {
        'Authorization': `JWT ${ process.env.NEXT_PUBLIC_BE_KEY }`
        , 'Content-Type': 'application/json'
        , ...(
            process.env.NEXT_PUBLIC_PATHFINDER_KEY
            && { 'x-pathfinder-name': process.env.NEXT_PUBLIC_PATHFINDER_KEY }
        )
    }
    , baseURL: process.env.NEXT_PUBLIC_BE_URL
} );

interface makeAPICallProps<TPayload> {
    url: string;
    method?: HttpMethod;
    data?: TPayload;
    errorPrefixMessage?: string;
}

export const makeAPICall = async <TData = any, TPayload = any>( {
    method
    , url
    , data
    , errorPrefixMessage
}: makeAPICallProps<TPayload> ): Promise<TData> => {

    let baseURL = `${ process.env.NEXT_PUBLIC_BE_URL }`;

    if ( process.env.NEXT_PUBLIC_PATHFINDER_KEY && ( process.env.NEXT_PUBLIC_PATHFINDER_BFF_TYPE === '' || process.env.NEXT_PUBLIC_PATHFINDER_BFF_TYPE === 'monolith' ) ) {
        baseURL = `${ baseURL }/${ process.env.NEXT_PUBLIC_PATHFINDER_KEY }`;
    }

    try {
        const response = await axiosInstance( {
            method: method || 'GET'
            , url
            , baseURL
            , data
        } );

        return response ? response.data : null;
    } catch ( e: any ) {
        let message = 'We experienced a network error. Please try again or contact support.';
        const response = e.response;

        if (
            url
            && url.indexOf( '/users/login' ) === -1
            && url.indexOf( '/users/check' ) === -1
            && url.indexOf( '/users/reset-password' ) === -1
            && response
            && response.status
            && ( response.status === 401 || response.status === 403 )
        ) {
            if ( response.data.code === 'SESSION_NOT_FOUND' ) {
                const store = window.store;
                store?.dispatch( updateSessionErrorCount() );
            }

            console.error( 'Unauthorized to retrieve data', response, window.location.pathname );
        }

        if ( response && response.data && ( response.data.clientMessage || response.data.message ) ) {
            message = ( response.data.clientMessage || response.data.message );

            if ( message.includes( '-' ) ) {
                const messageArr = message.split( '-' );
                message = messageArr[ messageArr.length - 1 ];
            }
        }

        throw new Error(
            errorPrefixMessage
                ? `${ errorPrefixMessage } - ${ message }`
                : message
            , { cause: response?.data?.code ?? 'Unknown' }
        );
    }
};

export interface MakeBFFAPICallProps<TPayload> {
    url: string;
    method?: HttpMethod;
    data?: TPayload;
    errorPrefixMessage?: string;
    params?: any; // axios uses any type for params
}

export const makeBFFAPICall = async <TData = any, TPayload = any>( {
    url
    , method = 'GET'
    , data
    , errorPrefixMessage
    , params
}: MakeBFFAPICallProps<TPayload> ): Promise<TData> => {

    let baseURL = `${ process.env.NEXT_PUBLIC_BE_URL }/business-portal-web`;

    if ( process.env.NEXT_PUBLIC_PATHFINDER_KEY && ( process.env.NEXT_PUBLIC_PATHFINDER_BFF_TYPE === '' || process.env.NEXT_PUBLIC_PATHFINDER_BFF_TYPE === 'mkt-bff' ) ) {
        baseURL = `${ baseURL }-${ process.env.NEXT_PUBLIC_PATHFINDER_KEY }`;
    }

    const config: AxiosRequestConfig<TPayload> = {
        method
        , url
        , baseURL
        , data
        , responseType: 'json'
        , headers: { 'Authorization': `JWT ${ process.env.NEXT_PUBLIC_BFF_KEY }` }
        , params
    };

    try {
        const response = await axiosInstance<TData>( config );
        return response.data;
    } catch ( error: any ) {
        let message = 'We experienced a network error. Please try again or contact support.';
        const response = error.response;

        if (
            url
            && url.indexOf( '/users/login' ) === -1
            && url.indexOf( '/users/check' ) === -1
            && url.indexOf( '/users/reset-password' ) === -1
            && response
            && response.status
            && ( response.status === 401 || response.status === 403 )
        ) {
            if ( response.data.code === 'SESSION_NOT_FOUND' ) {
                const store = window.store;
                store?.dispatch( updateSessionErrorCount() );
            }

            console.error( 'Unauthorized to retrieve data', response, window.location.pathname );
        }

        if ( response && response.data && ( response.data.clientMessage || response.data.message ) ) {
            message = ( response.data.clientMessage || response.data.message );

            if ( message.includes( '-' ) ) {
                const messageArr = message.split( '-' );
                message = messageArr[ messageArr.length - 1 ];
            }
        }

        throw new Error(
            errorPrefixMessage
                ? `${ errorPrefixMessage } - ${ message }`
                : message
            , { cause: response?.data?.code ?? 'Unknown' }
        );
    }
};

export default axiosInstance;
